import { defineStore } from "pinia";
import { ref, computed, watch, readonly } from "vue";

export type Theme = "light" | "dark" | "system";

export const useThemeStore = defineStore("theme", () => {
    // State
    const theme = ref<Theme>("system");
    const systemPreference = ref<"light" | "dark">("light");
    const isInitialized = ref(false);

    // Getters
    const isDark = computed(() => {
        if (theme.value === "system") {
            return systemPreference.value === "dark";
        }
        return theme.value === "dark";
    });

    const isLight = computed(() => !isDark.value);

    const currentTheme = computed(() => {
        if (theme.value === "system") {
            return systemPreference.value;
        }
        return theme.value;
    });

    // Actions
    const setTheme = (newTheme: Theme) => {
        theme.value = newTheme;
        saveThemeToStorage(newTheme);
        applyTheme(newTheme);
    };

    const toggleTheme = () => {
        const themes: Theme[] = ["light", "dark", "system"];
        const currentIndex = themes.indexOf(theme.value);
        const nextIndex = (currentIndex + 1) % themes.length;
        setTheme(themes[nextIndex]);
    };

    const setSystemPreference = (pref: "light" | "dark") => {
        systemPreference.value = pref;
        if (theme.value === "system") {
            applyTheme("system");
        }
    };

    const initializeTheme = () => {
        // Load theme from localStorage
        const savedTheme = loadThemeFromStorage();
        if (savedTheme) {
            theme.value = savedTheme;
        }

        // Detect system preference
        detectSystemPreference();

        // Apply initial theme
        applyTheme(theme.value);

        // Listen for system theme changes
        listenForSystemChanges();

        isInitialized.value = true;
    };

    // Helper functions
    const saveThemeToStorage = (themeValue: Theme) => {
        try {
            localStorage.setItem("lms-theme", themeValue);
        } catch (error) {
            console.warn("Failed to save theme to localStorage:", error);
        }
    };

    const loadThemeFromStorage = (): Theme | null => {
        try {
            const saved = localStorage.getItem("lms-theme");
            return (saved as Theme) || null;
        } catch (error) {
            console.warn("Failed to load theme from localStorage:", error);
            return null;
        }
    };

    const applyTheme = (themeValue: Theme) => {
        const html = document.documentElement;
        const targetTheme =
            themeValue === "system" ? systemPreference.value : themeValue;

        // Remove existing theme classes
        html.classList.remove("light", "dark");

        // Apply new theme class
        html.classList.add(targetTheme);

        // Update meta theme-color for mobile browsers
        updateMetaThemeColor(targetTheme);

        // Update CSS custom properties
        updateCSSVariables(targetTheme);
    };

    const detectSystemPreference = () => {
        if (
            window.matchMedia &&
            window.matchMedia("(prefers-color-scheme: dark)").matches
        ) {
            systemPreference.value = "dark";
        } else {
            systemPreference.value = "light";
        }
    };

    const listenForSystemChanges = () => {
        if (window.matchMedia) {
            const mediaQuery = window.matchMedia(
                "(prefers-color-scheme: dark)",
            );
            mediaQuery.addEventListener("change", (e) => {
                systemPreference.value = e.matches ? "dark" : "light";
                if (theme.value === "system") {
                    applyTheme("system");
                }
            });
        }
    };

    const updateMetaThemeColor = (themeValue: "light" | "dark") => {
        const themeColor = themeValue === "dark" ? "#1e293b" : "#ffffff";
        let metaThemeColor = document.querySelector<HTMLMetaElement>(
            'meta[name="theme-color"]',
        );

        if (!metaThemeColor) {
            metaThemeColor = document.createElement("meta");
            metaThemeColor.name = "theme-color";
            document.head.appendChild(metaThemeColor);
        }

        metaThemeColor.content = themeColor;
    };

    const updateCSSVariables = (themeValue: "light" | "dark") => {
        const root = document.documentElement;

        if (themeValue === "dark") {
            root.style.setProperty("--bg-primary", "rgb(30, 41, 59)");
            root.style.setProperty("--bg-secondary", "rgb(15, 23, 42)");
            root.style.setProperty("--text-primary", "rgb(248, 250, 252)");
            root.style.setProperty("--text-secondary", "rgb(148, 163, 184)");
            root.style.setProperty("--border-color", "rgb(51, 65, 85)");
        } else {
            root.style.setProperty("--bg-primary", "rgb(255, 255, 255)");
            root.style.setProperty("--bg-secondary", "rgb(248, 250, 252)");
            root.style.setProperty("--text-primary", "rgb(15, 23, 42)");
            root.style.setProperty("--text-secondary", "rgb(100, 116, 139)");
            root.style.setProperty("--border-color", "rgb(226, 232, 240)");
        }
    };

    // Watch for theme changes
    watch(theme, (newTheme) => {
        if (isInitialized.value) {
            applyTheme(newTheme);
        }
    });

    watch(systemPreference, () => {
        if (isInitialized.value && theme.value === "system") {
            applyTheme("system");
        }
    });

    return {
        // State
        theme: readonly(theme),
        systemPreference: readonly(systemPreference),
        isInitialized: readonly(isInitialized),

        // Getters
        isDark: readonly(isDark),
        isLight: readonly(isLight),
        currentTheme: readonly(currentTheme),

        // Actions
        setTheme,
        toggleTheme,
        setSystemPreference,
        initializeTheme,
    };
});
